import { ApolloClient, DocumentNode } from "@apollo/client";

import { LogsResponse } from "types/generated";

type FetchSanitizedLogsVariables = {
  stackId?: string;
  moduleId?: string;
  runId: string;
  state: string;
  stateVersion?: number | null;
};

type EntityLogsData =
  | { stack: { run: { logs: LogsResponse | null } | null } | null }
  | { module: { run: { logs: LogsResponse | null } | null } | null };

type SanitizedLogs = {
  text: string;
  exists: boolean;
  deleted: boolean;
  expired: boolean;
  ongoing: boolean;
};

const getLogs = (data: EntityLogsData | null | undefined) => {
  if (!data) return undefined;

  const entity = "module" in data ? data.module : data.stack;

  return entity?.run?.logs ?? undefined;
};

// A hard backstop against a misbehaving backend looping forever on hasMore: true.
const MAX_PAGES = 10_000;

// Pages are joined with "\n", mirroring how the backend itself joins filtered
// pages for the same underlying byte stream (shared/ai/logs/run_logs_reader.go).
export const fetchSanitizedLogs = async (
  client: ApolloClient,
  query: DocumentNode,
  variables: FetchSanitizedLogsVariables
): Promise<SanitizedLogs> => {
  const pages: string[] = [];
  let token: string | undefined;
  let lastLogs: LogsResponse | undefined;
  let pageCount = 0;

  do {
    const { data } = await client.query<EntityLogsData>({
      query,
      fetchPolicy: "no-cache",
      variables: token ? { ...variables, token } : variables,
    });

    const logs = getLogs(data);

    if (!logs) break;

    lastLogs = logs;
    pageCount++;

    const pageText = logs.messages.map((message) => message.message).join("");
    if (pageText) pages.push(pageText);

    token = logs.nextToken ?? undefined;
  } while (lastLogs?.hasMore && token && pageCount < MAX_PAGES);

  return {
    text: pages.join("\n"),
    exists: lastLogs?.exists ?? false,
    deleted: lastLogs?.deleted ?? false,
    expired: lastLogs?.expired ?? false,
    ongoing: lastLogs?.ongoing ?? false,
  };
};
